import { yupResolver } from '@hookform/resolvers/yup';
import { useContext, useEffect, useState } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import cx from 'classnames';

import { useLoading } from '@/components/common/loader/LoadingContext';
import ButtonCustom from '@/components/common/ui/button';
import { CalendarCustomForm } from '@/components/common/ui/calendar';
import { InputTextCustomForm } from '@/components/common/ui/input/Input';
import { TextareaCustomForm } from '@/components/common/ui/textarea';
import { useTranslation } from '@/config/i18n';
import useScrollToError from '@/hooks/useScrollToError';
import PATH from '@/routes/path';
import { GiftFormType, giftFormSchema } from '@/shared/schema/gift';
import { CustomRadioGroup } from '@/components/common/ui/radioGroup';
import { IconPlus } from '@/components/icons';
import { GiftTypeManagementDialog } from './GiftTypeManagementForm';
import { HTTP_STATUS, MAX_LENGTH } from '@/shared/constants';
import { giftQuantityType, giftStatus } from '@/shared/constants/gift';
import { apiRequest } from '@/services';
import { toastErrorMessage, toastMessage } from '@/lib/utils';
import { ToastContext } from '@/components/common/CommonToast';
import { GiftTypeResponseType } from '@/types/gift';
import { TreeSelectCustomForm } from '@/components/common/ui/treeSelect';
import { UploadVideo } from '@/components/common/ui/upload';

type Props = {
  id?: number;
};

const GiftForm: React.FC<Props> = ({ id }) => {
  const { toast } = useContext(ToastContext);
  const [t] = useTranslation('');
  const { apiService } = apiRequest();
  const [visibleFormGiftType, setVisibleFormGiftType] = useState(false);
  const [giftTypeList, setGiftTypeList] = useState<GiftTypeResponseType[]>([]);
  const [isLoadingGiftType, setIsLoadingGiftType] = useState(false);
  const [listVideos, setListVideos] = useState<any[]>([]);

  const navigate = useNavigate();
  const { scrollToError } = useScrollToError();
  const { loadingGlobal, setLoadingGlobal } = useLoading();

  const formMethods = useForm<GiftFormType>({
    mode: 'onChange',
    resolver: yupResolver(giftFormSchema),
    shouldFocusError: false,
    defaultValues: {
      name: '',
      gift_type_id: undefined,
      start_date: null,
      end_date: null,
      gift_quantity_type: 'limit',
      total_quantity: '',
      description: '',
      points_required_for_student: '',
      points_required_for_teacher: '',
      email_content: '',
      videos: [
        {
          name: '',
          duration: 0,
          thumbnail_id: null,
          video_id: null,
          thumbnail_path: '',
        },
      ],
    },
  });

  const {
    handleSubmit,
    control,
    setError,
    clearErrors,
    getValues,
    formState: { errors },
    watch,
    reset,
    setValue,
  } = formMethods;

  const { fields, append, remove } = useFieldArray({
    control: formMethods.control,
    name: 'videos',
  });

  const startDate = watch('start_date');
  const endDate = watch('end_date');

  async function getListGiftType() {
    setIsLoadingGiftType(true);
    const { data: giftTypes, error } = await apiService.giftTypes.query('');

    const giftTypeIdCurrent = getValues('gift_type_id');

    if (giftTypeIdCurrent) {
      const isExist = giftTypes?.data?.find(
        (item: GiftTypeResponseType) => item.id === giftTypeIdCurrent,
      );

      if (!isExist) {
        setValue('gift_type_id', null as unknown as number);
      }
    }

    setIsLoadingGiftType(false);

    if (error) return;

    setGiftTypeList(giftTypes?.data ?? []);
  }

  async function getGiftDetail(id: number) {
    setLoadingGlobal(true);
    const { data: gift } = await apiService.gifts.show(id);

    if (gift?.data) {
      reset({
        ...gift.data,
        gift_type_id: gift.data.gift_types?.id || undefined,
        start_date: gift.data.start_date
          ? new Date(gift.data.start_date)
          : null,
        end_date: gift.data.end_date ? new Date(gift.data.end_date) : null,
        gift_quantity_type: !gift.data.total_quantity
          ? giftQuantityType.noLimit
          : giftQuantityType.limit,
        total_quantity: gift.data.total_quantity || '',
        points_required_for_student:
          gift.data.points_required_for_student || '',
        points_required_for_teacher:
          gift.data.points_required_for_teacher || '',
        videos: gift.data.videos.length
          ? gift.data.videos
          : [{
            name: '',
            duration: 0,
            thumbnail_id: null,
            video_id: null,
            thumbnail_path: '',
          }],
      });
      setListVideos(gift.data.videos);
    }
    setLoadingGlobal(false);
  }

  function removeDefaultVideo(videos: any[]) {
    if (!videos.length) return videos;

    return videos.filter(
      (video: any) =>
        !(
          video.name === '' &&
          video.duration === 0 &&
          video.thumbnail_id === null &&
          video.video_id === null &&
          video.thumbnail_path === ''
        ),
    );
  }

  function getPayload(data: GiftFormType, status: string) {
    const formatDate = (date: Date | null | undefined) =>
      dayjs(date).isValid() ? dayjs(date).format('YYYY/MM/DD') : '';
    const oldVideos = listVideos;
    const newVideos = data.videos;

    const videosFiltered: any[] = [];
    newVideos?.forEach((video: any) => {
      const matchedItem = oldVideos.find(
        (item) => item.video_id === video.video_id,
      );

      if (!matchedItem) {
        videosFiltered.push(video);
        return;
      }

      if (matchedItem && video.new_video_id) {
        videosFiltered.push({
          ...video,
          old_thumbnail_id: matchedItem.thumbnail_id,
          old_video_id: matchedItem.video_id,
        });
        return;
      }

      if (matchedItem && !video.new_video_id) {
        videosFiltered.push({
          ...video,
        });
        return;
      }
    });

    oldVideos.forEach((video: any) => {
      const matchedItem = newVideos?.find(
        (item) => item.video_id === video.video_id,
      );

      if (!matchedItem) {
        videosFiltered.push({
          ...video,
          old_thumbnail_id: video.thumbnail_id,
          old_video_id: video.video_id,
        });
      }
    });

    return {
      ...data,
      status,
      start_date: formatDate(data.start_date),
      end_date: formatDate(data.end_date),
      points_required_for_student:
        data.points_required_for_student?.toString().replace(/,/g, '') ?? '',
      points_required_for_teacher:
        data.points_required_for_teacher?.toString().replace(/,/g, '') ?? '',
      total_quantity:
        data.gift_quantity_type === giftQuantityType.limit &&
          status === giftStatus.DRAFT &&
          data.total_quantity === ''
          ? '1'
          : data.total_quantity?.toString().replace(/,/g, '') ?? '',
      videos: id
        ? removeDefaultVideo(videosFiltered ?? [])
        : removeDefaultVideo(newVideos ?? []),
    };
  }

  const validateGiftForm = () => {
    const values = getValues();
    const errors: Record<string, string> = {};
  
    values.videos?.forEach((video: any, index: number) => {
      const { name, thumbnail_path } = video;
  
      if (!name && thumbnail_path) {
        errors[`videos.${index}.name`] = t('gift.required.video_name_required');
      }
  
      if (!thumbnail_path && name) {
        errors[`videos.${index}.thumbnail_path`] = t('gift.required.video_file_required');
      }
    });

    if (!values.name?.trim()) {
      errors['name'] = t('gift.name_required');
    }
  
    Object.entries(errors).forEach(([key, message]) => {
      setError(key as any, { message });
    });
  
    return Object.keys(errors).length === 0;
  };

  async function onSaveDraftGift() {
    setLoadingGlobal(true);
    clearErrors();
    const values = getValues();

    if (!validateGiftForm()) {
      setLoadingGlobal(false);
      scrollToError();
      return;
    }

    const payload = getPayload(values, giftStatus.DRAFT);

    if (id) {
      const { error: giftError } = await apiService.gifts.update(id, payload);
      if (giftError) {
        handleError(giftError);
        setLoadingGlobal(false);
        return;
      }
      toast?.current?.show(toastMessage(t('gift.edit_gift_success')));
      handleNavigateBack();
      return;
    }

    const { error: giftError } = await apiService.gifts.create(payload);
    if (giftError) {
      handleError(giftError);
      setLoadingGlobal(false);
      return;
    }

    toast?.current?.show(toastMessage(t('gift.save_gift_success')));
    handleNavigateBack();
  }

  async function onSavePublicGift(data: GiftFormType) {
    setLoadingGlobal(true);
    clearErrors();

    const payload = getPayload(data, giftStatus.PUBLIC);

    if (id) {
      const { error: giftError } = await apiService.gifts.update(id, payload);

      if (giftError) {
        handleError(giftError);
        setLoadingGlobal(false);
        return;
      }

      toast?.current?.show(toastMessage(t('gift.edit_gift_success')));
      handleNavigateBack();
      setLoadingGlobal(false);
      return;
    }

    const { error: giftError } = await apiService.gifts.create(payload);

    if (giftError) {
      handleError(giftError);
      setLoadingGlobal(false);
      return;
    }

    toast?.current?.show(toastMessage(t('gift.save_gift_success')));
    handleNavigateBack();
  }

  function handleNavigateBack() {
    navigate(PATH.gift);
  }

  function handleError(error: any) {
    const { status_code, errors, message } = error;
    if (status_code === HTTP_STATUS.FORM_ERROR) {
      errors.forEach((e: any) => {
        const { field, message } = e;
        setError(field, { message });
      });
    }

    const msg = message;
    toast?.current?.show(toastErrorMessage(msg));
  }

  useEffect(() => {
    if (watch('gift_quantity_type') === giftQuantityType.noLimit) {
      formMethods.setValue('total_quantity', '');
      formMethods.setError('total_quantity', {
        type: 'manual',
        message: '',
      });
    }
  }, [watch('gift_quantity_type')]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      scrollToError();
    }, 500);

    return () => {
      clearTimeout(timeout);
    };
  }, [errors]);

  useEffect(() => {
    if (!giftTypeList.length) {
      getListGiftType();
    }
  }, [giftTypeList]);

  useEffect(() => {
    if (id) {
      getGiftDetail(id);
    }
  }, [id]);

  return (
    <>
      <FormProvider {...formMethods}>
        <form className="mt-[16px]" autoComplete="off">
          <div className="form-item flex">
            <div className="label w-56 flex">
              <span>{t('gift.name')}</span>
              <span className="text-error-500">*</span>
            </div>
            <InputTextCustomForm
              name="name"
              wrapClass="w-[512px]"
              placeholder={t('gift.name_placeholder')}
            />
          </div>

          <div className="form-item flex mt-[20px]">
            <div className="label w-56 flex">
              <span>{t('gift.gift_type')}</span>
              <span className="text-error-500">*</span>
            </div>
            <div className="flex justify-between w-[512px]">
              <TreeSelectCustomForm
                name="gift_type_id"
                wrapClass="w-[460px]"
                panelClassName="tree-hide-header tree-disabled-unselect"
                optionsRender={giftTypeList.map(
                  (item: GiftTypeResponseType) => ({
                    label: item.name,
                    key: item.id,
                  }),
                )}
                disabled={isLoadingGiftType}
                isUsingOptionsRender
                panelStyle={{ width: '100%', maxWidth: '460px' }}
              />
              <button
                type="button"
                className=" bg-primary-700 rounded-lg w-[42px] h-[42px] flex justify-center items-center"
                onClick={() => setVisibleFormGiftType(true)}
              >
                <span className="text-white">
                  <IconPlus />
                </span>
              </button>
            </div>
          </div>

          <div className="form-item flex mt-[20px]">
            <div className="label w-56 flex">
              <span>{t('gift.start_date')}</span>
            </div>
            <div className="w-[512px]">
              <CalendarCustomForm
                wrapClass="w-full"
                name="start_date"
                dateFormat="yy/mm/dd"
                showIcon
                icon={() => <i className="pi pi-calendar text-[18px]" />}
                placeholder="yyyy/mm/dd"
                panelClassName="calendar-date-of-birth"
                minDate={new Date()}
                maxDate={endDate ? new Date(endDate ?? '') : undefined}
                showButtonBar
              />
            </div>
          </div>

          <div className="form-item flex mt-[20px]">
            <div className="label w-56 flex">
              <span>{t('gift.end_date')}</span>
            </div>
            <div className="w-[512px]">
              <CalendarCustomForm
                wrapClass="w-full"
                name="end_date"
                dateFormat="yy/mm/dd"
                minDate={startDate ? new Date(startDate ?? '') : undefined}
                showIcon
                icon={() => <i className="pi pi-calendar text-[18px]" />}
                placeholder="yyyy/mm/dd"
                panelClassName="calendar-date-of-birth"
                showButtonBar
              />
            </div>
          </div>

          <div className="form-item flex mt-[20px]">
            <div className="label w-56">
              <span>{t('gift.detail')}</span>
            </div>
            <div className="w-[512px]">
              <TextareaCustomForm
                name="description"
                wrapClass="w-full"
                maxLength={MAX_LENGTH.GIFT_DESCRIPTION}
                placeholder={t('gift.detail_placeholder')}
              />
            </div>
          </div>

          <div className="form-item flex mt-[20px]">
            <div className="label w-56 flex">
              <span>{t('gift.video')}</span>
            </div>
            <div className="bg-gray-100 px-4 w-[512px] rounded-lg border border-gray-300">
              {fields.map((video, index) => (
                <div
                  key={video.id}
                  className="relative border-t border-gray-300 first:border-t-0 py-4"
                >
                  <span>
                    {t('gift.name_video')}
                    {index + 1}
                  </span>
                  <div className="flex items-center gap-2 mt-2">
                    <div className="w-full">
                      <InputTextCustomForm
                        name={`videos.${index}.name`}
                        placeholder={t('gift.name_video_placeholder')}
                      />

                      <div className="mt-4">
                        <UploadVideo
                          className="relative flex-full-center border border-gray-300 w-full bg-white"
                          name={`videos.${index}.video_id`}
                          handleUploaded={({
                            currentFile,
                            thumbnail_id,
                            video_id,
                          }) => {
                            if (id) {
                              setValue(
                                `videos.${index}.new_thumbnail_id`,
                                Number(thumbnail_id),
                              );
                              setValue(
                                `videos.${index}.new_video_id`,
                                Number(video_id),
                              );
                              setValue(
                                `videos.${index}.thumbnail_path`,
                                currentFile.objectURL,
                              );
                              setValue(
                                `videos.${index}.duration`,
                                Math.round(currentFile.video_duration),
                              );
                              return;
                            }
                            setValue(
                              `videos.${index}.duration`,
                              Math.round(currentFile.video_duration),
                            );
                            setValue(
                              `videos.${index}.thumbnail_path`,
                              currentFile.objectURL,
                            );
                            setValue(
                              `videos.${index}.thumbnail_id`,
                              Number(thumbnail_id),
                            );
                            setValue(
                              `videos.${index}.video_id`,
                              Number(video_id),
                            );
                          }}
                          defaultUrl={fields[index].thumbnail_path || ''}
                          acceptedTypes={['video/mp4']}
                          idChooseBtn={`choose-video-${index}`}
                          textUpload={t('gift.text_upload')}
                          textSubUpload={t('gift.text_sub_upload')}
                          textUploadSuccess={t('gift.upload_video_success')}
                        />
                      </div>
                    </div>
                    <button
                      className={cx('p-[5px]', {
                        'cursor-not-allowed': fields.length === 1,
                      })}
                      type="button"
                      onClick={() => remove(index)}
                      disabled={fields.length === 1}
                    >
                      <span
                        className={cx('pi pi-trash text-error-500', {
                          'text-gray-400': fields.length === 1,
                        })}
                      />
                    </button>
                  </div>
                  {errors?.videos?.[index]?.name && (
                    <div className="text-error-500 mt-2">
                      {t('gift.required.video_name_required')}
                    </div>
                  )}
                  {errors?.videos?.[index]?.thumbnail_path && (
                    <div className="text-error-500 mt-2">
                      {t('gift.required.video_file_required')}
                    </div>
                  )}
                </div>
              ))}
              <button
                type="button"
                className={cx(
                  'flex gap-3 items-center py-4 border-t border-gray-300 w-full',
                  {
                    hidden: fields.length >= 5,
                    'border-t-0': fields.length === 0,
                  },
                )}
                onClick={() =>
                  append({
                    name: '',
                    duration: 0,
                    thumbnail_id: null,
                    video_id: null,
                    thumbnail_path: '',
                  })
                }
              >
                <div className="bg-primary-100 p-[10px] h-[40px] w-[40px] flex-full-center text-primary-700 rounded-full">
                  <IconPlus />
                </div>
                <span>{t('gift.add_video')}</span>
              </button>
            </div>
          </div>

          <div className="form-item flex mt-[20px]">
            <div className="label w-56 flex">
              <span>{t('gift.point_exchange')}</span>
              <span className="text-error-500">*</span>
            </div>
            <div className="p-4 bg-gray-100 w-[512px] rounded-lg">
              <div className="flex flex-col items-center gap-2">
                <div className="label w-full flex flex-col gap-2">
                  <span>{t('gift.participants')}</span>
                  <InputTextCustomForm
                    name="points_required_for_student"
                    placeholder={t('gift.point_exchange_placeholder')}
                    isNumber
                    preventStartZero
                    convertComma
                    maxLength={MAX_LENGTH.GIFT_POINT}
                    prefixIcon={
                      <span className="text-[16px] leading-[19px] text-gray-700 absolute right-[10px] top-[15px]">
                        {t('gift.point')}
                      </span>
                    }
                  />
                </div>
                <div className="label w-full flex flex-col gap-2">
                  <span>{t('gift.student_coach')}</span>
                  <InputTextCustomForm
                    name="points_required_for_teacher"
                    placeholder={t('gift.point_exchange_placeholder')}
                    isNumber
                    preventStartZero
                    convertComma
                    maxLength={MAX_LENGTH.GIFT_POINT}
                    prefixIcon={
                      <span className="text-[16px] leading-[19px] text-gray-700 absolute right-[10px] top-[15px]">
                        {t('gift.point')}
                      </span>
                    }
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="form-item flex mt-[20px]">
            <div className="label w-56 flex">
              <span>{t('gift.limit')}</span>
              <span className="text-error-500">*</span>
            </div>
            <div className="p-4 bg-gray-100 w-[512px] rounded-lg">
              <div className="flex flex-col items-center gap-4">
                <div className="label w-full flex justify-between">
                  <CustomRadioGroup
                    name="gift_quantity_type"
                    control={control}
                    options={[
                      {
                        label: t('gift.limit_exist'),
                        value: giftQuantityType.limit,
                      },
                      {
                        label: t('gift.no_limit'),
                        value: giftQuantityType.noLimit,
                      },
                    ]}
                  />
                </div>
                <div className="label w-full flex flex-col gap-2">
                  <InputTextCustomForm
                    name="total_quantity"
                    disabled={
                      watch('gift_quantity_type') === giftQuantityType.noLimit
                    }
                    placeholder={t('gift.limit_placeholder')}
                    isNumber
                    preventStartZero
                    maxLength={MAX_LENGTH.GIFT_POINT}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="form-item flex mt-[20px]">
            <div className="label w-56 flex">
              <span>{t('gift.mail_notification')}</span>
              <span className="text-error-500">*</span>
            </div>
            <div className="w-[512px]">
              <TextareaCustomForm
                name="email_content"
                wrapClass="w-full"
                maxLength={MAX_LENGTH.GIFT_EMAIL_CONTENT}
                placeholder={t('gift.mail_notification_placeholder')}
              />
            </div>
          </div>

          <div className="action flex justify-end mt-[25px] border-t border-gray-200 py-[18px] gap-5">
            <ButtonCustom
              className="w-[140px]"
              type="button"
              severity="secondary"
              onClick={() => handleNavigateBack()}
            >
              {t('gift.cancel')}
            </ButtonCustom>
            <ButtonCustom
              className="w-[140px] border-primary-700 text-primary-700"
              type="button"
              severity="secondary"
              onClick={onSaveDraftGift}
            >
              {t('gift.save_draft')}
            </ButtonCustom>
            <ButtonCustom
              className="w-[140px]"
              type="button"
              loading={loadingGlobal}
              onClick={() => handleSubmit(onSavePublicGift)()}
            >
              {t('gift.save')}
            </ButtonCustom>
          </div>

          {visibleFormGiftType && (
            <GiftTypeManagementDialog
              openPopup={visibleFormGiftType}
              setOpenPopup={() => setVisibleFormGiftType(!visibleFormGiftType)}
              giftType={giftTypeList}
              refreshDataGiftType={getListGiftType}
            />
          )}
        </form>
      </FormProvider>
    </>
  );
};

export default GiftForm;
